import React from 'react'
import { Reference } from '@apollo/client'
import { useDismissPlanAlertMutation } from '../../../../generated/graphql'

import { DetailsPlanAlert } from '../../../../types'
import AlertBanner from './AlertBanner'

type AlertBannerWrapperProps = {
  /** The alert to display */
  alert: DetailsPlanAlert
  /** The text to display on the delete button */
  deleteButtonLabel?: string
  /** Whether to disable the clear button */
  disableClear?: boolean
  /** Whether to disable the delete button */
  disableDelete?: boolean
  /** Whether to disable the dismiss button */
  disableDismiss?: boolean
  /** Callback even when the Clear button is clicked */
  onClear?: () => void
  /** Event callback when the delete button is clicked */
  onDelete?: () => void
  /** Event callback when the dismiss button is clicked in the Alert Banner */
  onDismiss?: (alert: DetailsPlanAlert) => void
  /** Whether to show the clear button */
  showClear?: boolean
  /** Whether to show the delete button  */
  showDelete?: boolean
  /** Whether to show the dismiss button  */
  showDismiss?: boolean
}

const defaultProps: Omit<AlertBannerWrapperProps, 'alert'> = {
  deleteButtonLabel: 'Delete',
  disableClear: false,
  disableDelete: false,
  disableDismiss: false,
  onClear: undefined,
  onDelete: undefined,
  onDismiss: undefined,
  showClear: false,
  showDelete: true,
  showDismiss: true
}

const AlertBannerWrapper = ({
  alert,
  deleteButtonLabel,
  disableClear,
  disableDelete,
  disableDismiss,
  onClear,
  onDelete,
  onDismiss,
  showClear,
  showDelete,
  showDismiss
}: AlertBannerWrapperProps) => {
  const [dismissAlert] = useDismissPlanAlertMutation()
  const handleDismiss = () => {
    const input = {
      alertKey: alert.alertKey,
      alertType: alert.alertType,
      planId: alert.planId,
      itemId: alert.itemId
    }
    dismissAlert({
      variables: { input },
      update: (cache) => {
        cache.modify({
          id: 'ROOT_QUERY',
          fields: {
            planAlerts(existingAlerts: Reference[], { readField }) {
              const curAlerts = existingAlerts || []
              // filtering by alertKey will remove all alerts attached to dupe courses
              return curAlerts.filter((ref) => alert.alertKey !== readField('alertKey', ref))
            },
            placeholderCourseDetail(existingPlaceholderCourseDetail: Reference, { readField }) {
              // The alert to remove might not be in the main planAlerts cache if the user selected a placeholder course
              // and hasn't yet saved the item.
              const curAlerts: Readonly<DetailsPlanAlert[]> =
                readField('alerts', existingPlaceholderCourseDetail) || []
              const newAlerts = curAlerts.filter(
                (ref) => alert.alertKey !== readField('alertKey', ref)
              )
              return { ...existingPlaceholderCourseDetail, alerts: newAlerts }
            }
          }
        })
      },
      onCompleted(data) {
        if (data?.dismissPlanAlert.success && onDismiss) {
          onDismiss(alert)
        }
      },
      optimisticResponse: {
        __typename: 'Mutation',
        dismissPlanAlert: {
          __typename: 'ResponseStatus',
          id: alert.alertKey,
          message: '',
          success: true
        }
      }
    })
  }
  return (
    <AlertBanner
      alert={alert}
      deleteButtonLabel={deleteButtonLabel}
      disableClear={disableClear}
      disableDelete={disableDelete}
      disableDismiss={disableDismiss}
      onClear={onClear}
      onDelete={onDelete}
      onDismiss={handleDismiss}
      showClear={showClear}
      showDelete={showDelete}
      showDismiss={showDismiss}
    />
  )
}

AlertBannerWrapper.defaultProps = defaultProps

export default AlertBannerWrapper
